/**
 * Qué permiso hace falta para entrar a cada pantalla del app operativo.
 *
 * Es la ÚNICA fuente de verdad para dos cosas: el menú lateral (qué se muestra) y
 * el guard de rutas (qué se deja abrir tipeando la URL). Antes eran dos listas
 * distintas —y la mayoría de las pantallas no estaba en ninguna—, así que un cajero
 * veía "Proveedores" en el menú y entraba para comerse un 403 del backend.
 *
 * Cada entrada es "alcanza con uno" (OR). Se busca por prefijo: la entrada más
 * larga que coincida gana, así `/app/informes/ventas/rentabilidad` pide el permiso
 * financiero aunque `/app/informes/ventas` pida sólo el de ventas.
 *
 * Una pantalla que no figura acá no pide nada extra: la cubren los guards de su
 * sección en app.routes.ts. El dueño pasa siempre (PermissionService).
 *
 * Los permisos son los mismos que exige el backend en routes/tenant.php: si una
 * pantalla pide acá algo que el endpoint no pide —o al revés— el usuario ve la
 * pantalla vacía o no ve una que podría usar.
 */
export const PERMISOS_PANTALLAS: Record<string, string[]> = {
  // Archivo
  '/app/archivo/clientes': ['clientes.ver'],
  '/app/archivo/vendedores': ['vendedores.ver'],
  '/app/archivo/listas-precios': ['precios.ver', 'precios.listas'],
  '/app/archivo/productos': ['articulos.ver'],
  '/app/archivo/familias': ['articulos.ver'],
  '/app/archivo/marcas': ['articulos.ver'],
  '/app/archivo/rubros': ['articulos.ver'],
  '/app/archivo/etiquetas': ['articulos.ver'],
  '/app/archivo/unidades-negocio': ['unidades_negocio.ver'],
  '/app/archivo/unidades-medida': ['unidades_medida.ver', 'articulos.ver'],
  '/app/archivo/depositos': ['depositos.ver'],
  '/app/archivo/proveedores': ['proveedores.ver'],
  '/app/archivo/rubros-iva-compras': ['compras.ver'],
  '/app/archivo/tarjetas': ['bancos.ver', 'configuracion.ver'],
  '/app/archivo/transportes': ['transportes.ver'],
  '/app/archivo/recargos-financiacion': ['configuracion.ver'],
  '/app/archivo/recargos-mora': ['configuracion.ver'],
  '/app/archivo/bancos-entidades': ['bancos.ver'],
  '/app/archivo/cuentas-bancarias': ['bancos.ver'],
  '/app/archivo/estados-cheques': ['cheques.ver', 'bancos.ver'],
  '/app/archivo/rubros-gastos': ['gastos.ver'],
  '/app/archivo/grupos-costos': ['gastos.ver'],
  '/app/archivo/paises': ['geografia.ver'],
  '/app/archivo/provincias': ['geografia.ver'],
  '/app/archivo/ciudades': ['geografia.ver'],
  '/app/archivo/tipos-turnos': ['configuracion.ver', 'caja.apertura'],
  '/app/archivo/socios': ['socios.ver'],

  // Ventas
  '/app/vender-pro': ['ventas.crear'],
  '/app/ventas/vender': ['ventas.crear'],
  '/app/ventas/punto-cobranzas': ['cobranzas.ver'],
  '/app/ventas/cobros-ctacte': ['cobranzas.ver'],
  '/app/ventas/gastos': ['caja.movimientos'],
  '/app/ventas/ficha-cliente': ['clientes.ver', 'cobranzas.ver'],
  '/app/ventas/comprobantes-fiscales': ['ventas.ver'],
  '/app/ventas/cajas': ['caja.ver', 'caja.apertura'],
  '/app/ventas/historial': ['ventas.ver'],
  '/app/ventas/presupuestos': ['ventas.ver'],
  '/app/ventas/remitos': ['ventas.ver'],
  '/app/ventas/notas-credito': ['ventas.ver'],
  '/app/ventas/notas-debito': ['ventas.ver'],
  '/app/ventas/preventas-pendientes': ['ventas.aprobar'],

  // Stock
  '/app/stock/consulta': ['stock.ver'],
  '/app/stock/ficha-producto': ['stock.ver'],
  '/app/stock/arqueos': ['stock.ver'],
  '/app/stock/entradas': ['stock.ajustar'],
  '/app/stock/salidas': ['stock.ajustar'],
  '/app/stock/transferencias-depositos': ['stock.ver'],

  // Bancos
  '/app/bancos/movimientos': ['bancos.ver', 'bancos.movimientos'],
  '/app/bancos/cheques-tercero': ['cheques.ver'],

  // Informes (encima del piso informes.ver de la sección)
  '/app/informes/ventas': ['informes.ventas'],
  '/app/informes/ventas/rentabilidad': ['informes.financieros'],
  '/app/informes/stock': ['informes.stock'],
  '/app/informes/stock/valorizacion': ['informes.financieros', 'costos.ver'],
  '/app/informes/cobranzas': ['cobranzas.ver', 'informes.financieros'],
  '/app/informes/pagos': ['pagos.ver', 'compras.ver'],
  '/app/informes/tesoreria': ['caja.ver', 'informes.financieros'],
  '/app/informes/gastos': ['gastos.ver', 'informes.financieros'],
  '/app/informes/gerenciales': ['informes.financieros'],
  '/app/informes/iva': ['informes.financieros'],
  '/app/informes/listados/articulos': ['articulos.ver'],
  '/app/informes/listados/precios': ['precios.ver'],
  '/app/informes/listados/etiquetas': ['precios.ver'],
  '/app/informes/listados/clientes': ['clientes.ver'],
  '/app/informes/listados/proveedores': ['proveedores.ver'],

  // Administración
  '/app/administracion/ajustes-precios': ['precios.ajuste_masivo', 'costos.ajuste_masivo'],
  '/app/administracion/facturas': ['compras.ver'],
  '/app/administracion/pagos-proveedores': ['pagos.ver'],
  '/app/administracion/cuenta-proveedores': ['proveedores.ver', 'pagos.ver'],
  '/app/administracion/gastos': ['gastos.ver'],
  '/app/administracion/notas-credito': ['compras.ver'],
  '/app/administracion/notas-debito': ['compras.ver'],
  '/app/administracion/sueldos': ['gastos.ver'],
  '/app/administracion/personal': ['gastos.ver'],

  // Herramientas
  '/app/herramientas/importar-precios': ['articulos.crear', 'articulos.editar', 'precios.editar'],
  '/app/herramientas/exportar-datos': ['articulos.ver', 'clientes.ver', 'proveedores.ver'],
  '/app/herramientas/importar-datos': ['articulos.crear', 'articulos.editar', 'clientes.crear', 'clientes.editar', 'proveedores.crear', 'proveedores.editar', 'stock.ajustar'],

  // Configuración
  '/app/configuracion/usuarios': ['usuarios.ver'],
  '/app/configuracion/sucursales': ['sucursales.ver'],
  '/app/configuracion/puntos-venta': ['puntos_venta.ver'],
  '/app/configuracion/roles': ['roles.ver'],
  '/app/configuracion/preferencias': ['configuracion.ver'],
  '/app/configuracion/auditoria': ['auditoria.ver'],
  '/app/configuracion/datos-fiscales': ['configuracion.ver'],
};

/** Permisos (OR) de la pantalla: la entrada de prefijo más largo que coincida. */
export function permisosDePantalla(url: string): string[] | null {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  let mejor: string | null = null;
  for (const clave of Object.keys(PERMISOS_PANTALLAS)) {
    if ((path === clave || path.startsWith(clave + '/')) && (!mejor || clave.length > mejor.length)) {
      mejor = clave;
    }
  }
  return mejor ? PERMISOS_PANTALLAS[mejor] : null;
}

/**
 * Filtra las tarjetas de un índice de sección (Archivo, Ventas, Stock...) con la
 * misma tabla, y saca las secciones que quedan vacías.
 */
export function seccionesVisibles<T extends { cards: { path: string }[] }>(
  secciones: T[],
  puede: (permisos: string[]) => boolean,
): T[] {
  return secciones
    .map(s => ({
      ...s,
      cards: s.cards.filter(c => {
        const permisos = permisosDePantalla(c.path);
        return !permisos || puede(permisos);
      }),
    }))
    .filter(s => s.cards.length > 0);
}

/**
 * Módulo del PLAN que necesita cada pantalla (tarea 228). Mismo criterio de prefijo
 * que los permisos, y los mismos módulos que exige el backend con plan.limits.
 */
export const MODULOS_PANTALLAS: Record<string, string> = {
  '/app/administracion/facturas': 'compras',
  '/app/administracion/pagos-proveedores': 'compras',
  '/app/administracion/cuenta-proveedores': 'compras',
  '/app/administracion/notas-credito': 'compras',
  '/app/administracion/notas-debito': 'compras',
  '/app/administracion/sueldos': 'personal',
  '/app/administracion/personal': 'personal',
  '/app/archivo/proveedores': 'proveedores',
  '/app/stock/arqueos': 'stock',
  '/app/stock/transferencias-depositos': 'stock',
  '/app/stock/entradas': 'stock',
  '/app/stock/salidas': 'stock',
  '/app/ventas/punto-cobranzas': 'cuentas_corrientes',
  '/app/ventas/cobros-ctacte': 'cuentas_corrientes',
  '/app/ventas/comprobantes-fiscales': 'facturacion_electronica',
  '/app/configuracion/puntos-venta-afip': 'facturacion_electronica',
  '/app/informes': 'reportes_basicos',
  '/app/informes/pagos': 'compras',
  '/app/informes/ventas/rentabilidad': 'reportes_avanzados',
  '/app/informes/stock/valorizacion': 'reportes_avanzados',
  '/app/informes/gerenciales': 'reportes_avanzados',
  '/app/informes/iva': 'libro_iva',
  '/app/bancos': 'bancos',
  '/app/archivo/bancos-entidades': 'bancos',
  '/app/archivo/cuentas-bancarias': 'bancos',
  '/app/archivo/estados-cheques': 'bancos',
  '/app/ventas/presupuestos': 'presupuestos',
  '/app/ventas/remitos': 'presupuestos',
  '/app/ventas/preventas-pendientes': 'preventa',
  '/app/configuracion/auditoria': 'auditoria',
  '/app/configuracion/roles/nuevo': 'roles',
  '/app/administracion/ajustes-precios': 'ajuste_masivo',
  '/app/herramientas/importar-precios': 'importar_exportar',
  '/app/herramientas/exportar-datos': 'importar_exportar',
};

/** Nombre para mostrar de cada módulo del plan. */
export const NOMBRES_MODULOS: Record<string, string> = {
  compras: 'Compras y pagos a proveedores',
  proveedores: 'Proveedores',
  stock: 'Stock avanzado (arqueos, transferencias, entradas y salidas)',
  cuentas_corrientes: 'Cuentas corrientes y cobranzas',
  facturacion_electronica: 'Facturación electrónica (ARCA)',
  reportes_basicos: 'Informes',
  reportes_avanzados: 'Informes gerenciales y rentabilidad',
  libro_iva: 'Libro de IVA',
  bancos: 'Bancos y cheques',
  presupuestos: 'Presupuestos y remitos',
  comandas: 'Comandas (mostrador → caja)',
  preventa: 'Preventa y aprobación de pedidos',
  direccion: 'Tablero de dirección',
  roles: 'Roles a medida',
  auditoria: 'Registro de acciones',
  ajuste_masivo: 'Ajuste masivo de precios',
  impresion_a4: 'Impresión en media hoja y A4',
  importar_exportar: 'Importar precios del proveedor y exportar datos',
  listas_derivadas: 'Listas de precios derivadas de otra',
  recordatorios: 'Recordatorios de deuda en lote',
  personal: 'Personal y sueldos',
  imagenes_articulo: 'Más imágenes por artículo',
  sucursales: 'Más sucursales',
  ventas_monto_mes: 'Más ventas por mes',
  productos: 'Más artículos',
  cajas: 'Más cajas',
  depositos: 'Más depósitos',
  listas_precios: 'Más listas de precios',
};

export function moduloDePantalla(url: string): string | null {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  let mejor: string | null = null;
  for (const clave of Object.keys(MODULOS_PANTALLAS)) {
    if ((path === clave || path.startsWith(clave + '/')) && (!mejor || clave.length > mejor.length)) {
      mejor = clave;
    }
  }
  return mejor ? MODULOS_PANTALLAS[mejor] : null;
}
